import MobileModalFrame from "../common-components/mobile-modal-frame";
import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { deleteShapeExceptionThunk, saveShapeExceptionThunk } from "Redux/middlwares/supplierPricing";
import { getDefaultRebars } from "Services/rebar";
import { getSupplierCustomers } from "Services/supplier-customers";
import { ShapeException } from "../../utils/supplier-pricing-engine";
import { PBtn, PCard, PField, PNumber, PPill, PSegmented, PSelect, fmt, toNum } from "./ui";

const EMPTY: ShapeException = { shape: 0, chargeType: "bend", processingPerTon: null, unitPrice: null, customers: [] };

/** Catalog shapes = the same list the rebar editor shows (rebar-grouping). */
export const useCatalogShapes = () => {
  const [shapes, setShapes] = useState<{ id: number; name: string; number: string }[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const all: any[] = [];
      let cursor = "";
      for (let i = 0; i < 10; i++) {
        const page: any = await getDefaultRebars(cursor);
        (page?.results || []).forEach((g: any) => all.push(...(g.rebars || [])));
        if (!page?.next) break;
        // rebar-grouping uses page-number pagination; getDefaultRebars only takes the cursor value.
        const m = /[?&](cursor|page)=([^&]+)/.exec(String(page.next));
        if (!m) break;
        cursor = m[1] === "cursor" ? m[2] : `&page=${m[2]}`;
      }
      if (!cancelled) setShapes(all.map((r) => ({ id: r.id, name: r.name || `#${r.id}`, number: r.tool_number || String(r.id) })));
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return shapes;
};

export const useSupplierCustomers = () => {
  const [customers, setCustomers] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    getSupplierCustomers().then((r: any) => setCustomers(r?.data?.results || r?.data || []));
  }, []);
  return customers;
};

function ShapeExceptionModal({ show, onHide, initial }: { show: boolean; onHide: () => void; initial: ShapeException | null }) {
  const { t } = useTranslation(["supplierPricing"]);
  const dispatch = useAppDispatch();
  const shapes = useCatalogShapes();
  const customers = useSupplierCustomers();
  const [v, setV] = useState<ShapeException>(initial || EMPTY);
  useEffect(() => setV(initial || EMPTY), [initial, show]);
  const scope = v.customers.length ? "selected" : "all";
  const [pickCustomers, setPickCustomers] = useState(false);
  useEffect(() => setPickCustomers(scope === "selected"), [show]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal show={show} onHide={onHide} centered dialogClassName="eb-pr eb-pr-modal">
<MobileModalFrame onClose={onHide}>
      <div className="eb-pr-modal-body">
        <h2 className="eb-pr-modal-title">{t("exceptions.modalTitle")}</h2>
        <PField label={t("exceptions.shape")}>
          <PSelect
            value={v.shape || ""}
            onChange={(x) => setV({ ...v, shape: Number(x) })}
            options={[{ value: "", label: "—" }, ...shapes.map((s) => ({ value: s.id, label: `${s.name} · ${s.number}` }))]}
          />
        </PField>
        <div className="eb-pr-row">
          <PField label={t("exceptions.chargeType")} style={{ flex: 1 }}>
            <PSelect
              value={v.chargeType}
              onChange={(x) => setV({ ...v, chargeType: x as any })}
              options={["bend", "cut", "unit", "none"].map((c) => ({ value: c, label: t(`charge.${c}`) }))}
            />
          </PField>
          <PField label={t("exceptions.processing")} style={{ flex: 1 }}>
            <PNumber
              value={v.processingPerTon}
              disabled={v.chargeType === "unit" || v.chargeType === "none"}
              onChange={(x) => setV({ ...v, processingPerTon: toNum(x) })}
              suffix={t("units.perTon")}
            />
          </PField>
        </div>
        <PField label={t("exceptions.unitPrice")}>
          <PNumber value={v.unitPrice} disabled={v.chargeType !== "unit"} onChange={(x) => setV({ ...v, unitPrice: toNum(x) })} suffix={t("units.ils")} />
        </PField>
        <PField label={t("exceptions.appliesTo")}>
          <PSegmented
            value={pickCustomers ? "selected" : "all"}
            onChange={(s) => {
              setPickCustomers(s === "selected");
              if (s === "all") setV({ ...v, customers: [] });
            }}
            options={[
              { value: "all", label: t("exceptions.all") },
              { value: "selected", label: t("exceptions.selected") },
            ]}
          />
        </PField>
        {pickCustomers && (
          <div className="eb-pr-row" style={{ flexWrap: "wrap" }}>
            {customers.map((c) => {
              const on = v.customers.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  className={`eb-pr-pill ${on ? "eb-pr-pill--blue" : ""}`}
                  style={{ border: 0, cursor: "pointer" }}
                  onClick={() => setV({ ...v, customers: on ? v.customers.filter((x) => x !== c.id) : [...v.customers, c.id] })}
                >
                  {on ? "✓ " : "+ "}
                  {c.name}
                </button>
              );
            })}
          </div>
        )}
        <div className="eb-pr-footer">
          <PBtn kind="secondary" onClick={onHide}>
            {t("common.cancel")}
          </PBtn>
          <PBtn disabled={!v.shape || (pickCustomers && v.customers.length === 0)} onClick={() => dispatch(saveShapeExceptionThunk(v, onHide))}>
            {t("exceptions.save")}
          </PBtn>
        </div>
      </div>
    </MobileModalFrame>
</Modal>
  );
}

export default function ShapeExceptions() {
  const { t } = useTranslation(["supplierPricing"]);
  const dispatch = useAppDispatch();
  const exceptions = useAppSelector((s) => s.supplierPricing.draft?.shapeExceptions || []);
  const customers = useSupplierCustomers();
  const [edit, setEdit] = useState<ShapeException | null>(null);
  const [open, setOpen] = useState(false);

  const openEditor = (exception: ShapeException) => {
    setEdit(exception);
    setOpen(true);
  };

  const deleteException = (exception: ShapeException) => {
    if (
      exception.id &&
      window.confirm(t("exceptions.confirmDelete"))
    ) {
      dispatch(deleteShapeExceptionThunk(exception.id));
    }
  };

  const scopeFor = (exception: ShapeException) =>
    exception.customers.length === 0
      ? [t("exceptions.all")]
      : exception.customers.map(
          (id) => customers.find((customer) => customer.id === id)?.name || "#" + id
        );

  return (
    <PCard
      title={t("exceptions.title")}
      subtitle={t("exceptions.subtitle")}
      actions={
        <PBtn
          kind="secondary"
          size="sm"
          onClick={() => {
            setEdit(null);
            setOpen(true);
          }}
        >
          {t("exceptions.add")}
        </PBtn>
      }
    >
      <table className="eb-pr-table eb-pr-exceptions-table">
        <thead>
          <tr>
            <th>{t("exceptions.shape")}</th>
            <th>{t("exceptions.chargeType")}</th>
            <th>{t("exceptions.processing")}</th>
            <th>{t("exceptions.unitPrice")}</th>
            <th>{t("exceptions.appliesTo")}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {exceptions.map((e) => (
            <tr key={e.id}>
              <td>
                <b>{e.shapeName || `#${e.shape}`}</b> <span className="eb-pr-muted">{e.shapeNumber}</span>
              </td>
              <td>{t(`charge.${e.chargeType}`)}</td>
              <td>{e.processingPerTon !== null ? `${fmt(e.processingPerTon, 0)} ${t("units.perTon")}` : "—"}</td>
              <td>{e.unitPrice !== null ? `${fmt(e.unitPrice)} ${t("units.ils")}` : "—"}</td>
              <td>
                <div className="eb-pr-row" style={{ flexWrap: "wrap" }}>
                  {e.customers.length === 0 ? (
                    <PPill>{t("exceptions.all")}</PPill>
                  ) : (
                    e.customers.map((id) => (
                      <PPill key={id} tone="blue">
                        {customers.find((c) => c.id === id)?.name || `#${id}`}
                      </PPill>
                    ))
                  )}
                </div>
              </td>
              <td>
                <div className="eb-pr-row">
                  <button
                    type="button"
                    className="eb-pr-link"
                    onClick={() => openEditor(e)}
                  >
                    {t("common.edit")}
                  </button>
                  <button
                    type="button"
                    className="eb-pr-link"
                    style={{ color: "#c0392b" }}
                    onClick={() => deleteException(e)}
                  >
                    {t("common.delete")}
                  </button>
                </div>
              </td>
            </tr>
          ))}
          {exceptions.length === 0 && (
            <tr>
              <td colSpan={6} className="eb-pr-muted">
                —
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="eb-pr-exception-cards">
        {exceptions.map((exception) => (
          <article className="eb-pr-exception-card" key={exception.id}>
            <header className="eb-pr-exception-card__header">
              <div className="eb-pr-exception-card__shape">
                <span className="eb-pr-exception-card__preview" aria-hidden="true">
                  {exception.shapeNumber || exception.shape}
                </span>
                <div>
                  <strong>{exception.shapeName || "#" + exception.shape}</strong>
                  <span>{exception.shapeNumber}</span>
                </div>
              </div>
            </header>
            <dl className="eb-pr-exception-card__details">
              <div>
                <dt>{t("exceptions.chargeType")}</dt>
                <dd>{t("charge." + exception.chargeType)}</dd>
              </div>
              <div>
                <dt>{t("exceptions.processing")}</dt>
                <dd>
                  {exception.processingPerTon !== null
                    ? fmt(exception.processingPerTon, 0) + " " + t("units.perTon")
                    : "—"}
                </dd>
              </div>
              <div>
                <dt>{t("exceptions.unitPrice")}</dt>
                <dd>
                  {exception.unitPrice !== null
                    ? fmt(exception.unitPrice) + " " + t("units.ils")
                    : "—"}
                </dd>
              </div>
              <div className="eb-pr-exception-card__scope">
                <dt>{t("exceptions.appliesTo")}</dt>
                <dd>
                  {scopeFor(exception).map((scope, index) => (
                    <PPill
                      key={(exception.id || exception.shape) + "-scope-" + index}
                      tone={exception.customers.length ? "blue" : undefined}
                    >
                      {scope}
                    </PPill>
                  ))}
                </dd>
              </div>
            </dl>
            <div className="eb-pr-exception-card__actions">
              <button
                type="button"
                className="eb-pr-exception-action"
                onClick={() => openEditor(exception)}
              >
                {t("common.edit")}
              </button>
              <button
                type="button"
                className="eb-pr-exception-action eb-pr-exception-action--delete"
                onClick={() => deleteException(exception)}
              >
                {t("common.delete")}
              </button>
            </div>
          </article>
        ))}
        {exceptions.length === 0 && (
          <div className="eb-pr-exception-card eb-pr-muted">—</div>
        )}
      </div>
      <ShapeExceptionModal show={open} initial={edit} onHide={() => setOpen(false)} />
    </PCard>
  );
}
